import { createAsyncAction } from 'react-redux-await-control';
import { message } from 'antd';

import { getQueryParams } from '~/common';
import { telli, backoffice } from '~/services';
import dialogErrors from '~/common/dialogErrors';
import { handleErrors } from '~/common/utils/errorHandler';
import { InsuranceContractDetail } from '~/typings/entities/InsuranceContractDetail';
import { ContractCondition, ContractConditionsResult } from '~/typings/entities/ContractCondition';
import { ContractTermsResult } from '~/typings/entities/ContractTerms';
import {
  AdvancePage,
  AdvanceStatusCatalog,
  AdvanceTermHistoryEntry,
  AdvanceTermProofOfPayment,
} from '~/typings/entities/Advances';
import { parseGuaranteesResponse } from './guaranteesResponseParser';

const formatParam = (param) => (Array.isArray(param) ? param.join(',') : param);

export const searchContracts = createAsyncAction('SEARCH_CONTRACTS', {
  saveResult: true,
  initialValue: {},
  context: {
    responseParser: ({ data }) => data.resource,
    request: ({ tagTypes, loanTypes, ...params }) =>
      telli.get(`/admin/contractcustom/contracts`, {
        params: {
          tagTypes: formatParam(tagTypes),
          loanTypes: formatParam(loanTypes),
          ...params,
        },
      }),
  },
});

export const simulateRefin = createAsyncAction('SIMULATE_REFIN', {
  saveResult: true,
  context: {
    request: ({ id, ...data }) => telli.post(`/admin/contractcustom/contracts/${id}/simulate`, data),
  },
});

export const updateContractValues = createAsyncAction('UPDATE_CONTRACT_VALUES', {
  context: {
    request: ({ id, ...data }) => telli.post(`/admin/contractcustom/contracts/${id}/updatecontractvalues`, data),
  },
});

export const getUserActiveToQueue = createAsyncAction('GET_USER_ACTIVE_TO_QUEUE', {
  initialValue: null,
  saveResult: true,
  context: {
    request: ({ userId }) => telli.get(`/admin/contractcustom/useractivetoqueue/${userId}`),
  },
});

export const checkUserContractQueueStatus = createAsyncAction('CHECK_USER_CONTRACT_QUEUE_STATUS', {
  initialValue: false,
  saveResult: true,
  context: {
    request: ({ userId }) => telli.get(`/admin/contractcustom/checkactiveuserqueue/${userId}`),
  },
});

export const updateActiveUserQueue = createAsyncAction('UPDATE_ACTIVE_USER_QUEUE', {
  context: {
    request: ({ userId, ...rest }) => telli.post(`/admin/contractcustom/updateactiveuserqueue/${userId}`, rest),
  },
});

export const activateUserContractQueueStatus = createAsyncAction('ACTIVATE_USER_CONTRACT_QUEUE', {
  context: {
    request: (data) => {
      const { userId } = data;
      return telli.post(`/admin/contractcustom/activeuserqueue/${userId}`, data);
    },
  },
});

export const deactivateUserContractQueueStatus = createAsyncAction('DEACTIVATE_USER_CONTRACT_QUEUE', {
  context: {
    request: ({ userId }) => telli.post(`/admin/contractcustom/desativeuserqueue/${userId}`),
  },
});

export const listQueueContracts = createAsyncAction('LIST_QUEUE_CONTRACTS', {
  initialValue: [],
  saveResult: true,
  context: {
    request: ({ product }) => telli.get(`/admin/contractcustom/contractstoqueuebyuser`, { params: { product } }),
    errorParser: () => [],
  },
});

export const getContractQueueInformation = createAsyncAction('GET_CONTRACT_QUEUE_INFO', {
  initialValue: { contractAmount: 0, contractChecked: 0, inConference: 0 },
  saveResult: true,
  context: {
    request: (payload) =>
      telli.get(`/admin/contractcustom/contractQueueInformation`, {
        params: {
          type: payload?.type,
          kycStatus: payload?.kycStatus,
        },
      }),
    errorParser: () => [],
  },
});

export const getContractQueueInformationManual = createAsyncAction('GET_CONTRACT_QUEUE_INFO_MANUAL', {
  initialValue: { contractAmount: 0, contractChecked: 0, inConference: 0 },
  saveResult: true,
  context: {
    request: () => telli.get('/admin/contractcustom/contractQueueInformation'),
    errorParser: () => [],
  },
});

export const getContractToQueue = createAsyncAction('GET_CONTRACT_TO_QUEUE', {
  context: {
    request: (data) => telli.post('/admin/contractcustom/contractstoqueue', data),
  },
});

export const clearQueue = createAsyncAction('CLEAR_CONTRACT_QUEUE', {
  context: {
    request: () => telli.post('/admin/contractcustom/clearqueue'),
  },
});

export const checkQueueContract = createAsyncAction('CHECK_QUEUE_CONTRACT', {
  context: {
    request: (params) => telli.post(`/admin/contractcustom/checkcontracttoqueue?${getQueryParams(params)}`, {}),
  },
});

export const updateEventReturn = createAsyncAction('UPDATE_EVENT_RETURN', {
  context: {
    request: ({ contractId, eventId }) =>
      telli.post(`/admin/contractcustom/contracts/${contractId}/reprocesseventparati`, {
        paratiReturnEventIds: [eventId],
      }),
  },
});

export const listReturnsParati = createAsyncAction('LIST_RETURNS_PARATI', {
  saveResult: true,
  initialValue: {
    paratiAccount: null,
    paratiAccountReturns: null,
    paratiPreProposalReturns: [],
    paratiPreProposals: [],
    paratiProposalReturns: [],
    paratiProposals: [],
  },
  context: {
    request: ({ contractId }) => telli.get(`/admin/contractcustom/contracts/${contractId}/consigneereturns`),
  },
});

export const checkContractInConference = createAsyncAction('CHECK_CONTRACT_IN_CONFERENCE', {
  initialValue: { inConference: false, user: null, contractQueue: null },
  saveResult: true,
  context: {
    request: ({ id }) => telli.get(`/admin/contractcustom/contracts/${id}/inconference`),
  },
});

export const forceSendToQueue = createAsyncAction('FORCE_SEND_CONTRACT_TO_QUEUE', {
  context: {
    request: ({ id }) => telli.post(`/admin/contractcustom/forcesendcontracttoqueue?contractId=${id}`),
  },
});

export const advanceRequestQueue = createAsyncAction('ADVANCE_REQUEST_QUEUE', {
  context: {
    resultValidator: (response) => response?.data?.resource?.length,
    request: (data) => telli.post('/admin/contractcustom/contractstoqueue', data),
  },
});

export const getContractLogs = createAsyncAction('GET_CONTRACT_LOGS', {
  saveResult: true,
  initialValue: [],
  context: {
    request: ({ id }) => telli.get(`/admin/contractcustom/contracts/${id}/log`),
    onRequestError: () => message.error('Erro ao tentar carregar histórico do contrato'),
    errorParser: () => [],
  },
});

export const getContractLogsByExternalID = createAsyncAction('GET_CONTRACT_LOGS_BY_EXTERNAL_ID', {
  saveResult: true,
  initialValue: [],
  context: {
    request: ({ externalId, product }) => backoffice.get(`/contracts/history`, { params: { externalId, product } }),
    onRequestError: () => message.error('Erro ao tentar carregar histórico do contrato'),
    responseParser: ({ data }) => data,
    errorParser: () => [],
  },
});

export const generateDED = createAsyncAction('GENERATE_DED', {
  saveResult: true,
  context: {
    request: ({ contractId }) => telli.get(`/admin/contractcustom/${contractId}/ded`, { responseType: 'blob' }),
    responseParser: (response) => response.data,
    onRequestError: (err: any) =>
      handleErrors(err, {
        defaultMessage: 'Não foi possível realizar a solicitação',
        duration: 5,
      }),
  },
});

export const getContractDetails = createAsyncAction('GET_CONTRACT_DETAILS', {
  saveResult: true,
  initialValue: {
    id: false,
    loanType: 'NEW',
  },
  context: {
    request: ({ id }) => telli.get(`/admin/contractloancustom/loans/${id}`),
    responseParser: (response, { id }) => {
      const portOutStepLogs = response?.data?.resource?.portOutStepLogsDTO || response?.data?.resource?.portOutStepLogs;

      return {
        ...response.data.resource,
        contractId: id,
        portOutStepLogs,
      };
    },
  },
});

export const getContractGuarantees = createAsyncAction('GET_CONTRACT_GUARANTEES', {
  saveResult: true,
  initialValue: null,
  context: {
    request: ({ id }) => telli.get(`/admin/contractloancustom/loans/${id}/guarantees`),
    responseParser: parseGuaranteesResponse,
    errorParser: () => null,
  },
});

/**
 * O endpoint de condicoes devolve DUAS coisas: a `baseCcb` -- a CCB do proprio contrato -- e as
 * `conditions`, que sao as versoes geradas por refinanciamento.
 *
 * Contrato sem refin tem `conditions: []` e a baseCcb preenchida. Descartar a baseCcb, como este
 * parser fazia, deixava a tela sem nenhuma CCB nesse caso -- e quem depende dela (antecipacao de
 * recebiveis) simplesmente nao carregava, sem dizer por que.
 */
export const parseContractConditionsResponse = ({
  data,
}: {
  data?: { resource?: { baseCcb?: string; conditions?: ContractCondition[] } };
}): ContractConditionsResult => ({
  baseCcb: data?.resource?.baseCcb,
  conditions: data?.resource?.conditions || [],
});

export const getContractConditions = createAsyncAction('GET_CONTRACT_CONDITIONS', {
  saveResult: true,
  initialValue: { baseCcb: undefined, conditions: [] } as ContractConditionsResult,
  context: {
    request: ({ id }) => telli.get(`/admin/contractloancustom/loans/${id}/conditions`),
    responseParser: parseContractConditionsResponse,
    errorParser: () => ({ baseCcb: undefined, conditions: [] }),
  },
});

export const parseRefundResourceResponse = ({ data }: { data?: { resource?: unknown } }) => data?.resource || null;

export const getRefundAllocations = createAsyncAction('GET_REFUND_ALLOCATIONS', {
  saveResult: true,
  initialValue: null,
  context: {
    request: ({ id, ccb }) =>
      telli.get(`/admin/contractloancustom/loans/${id}/refund-allocations`, {
        params: ccb ? { ccb } : undefined,
        timeout: 15000,
      }),
    responseParser: parseRefundResourceResponse,
    errorParser: () => null,
  },
});

export const getRefundRequests = createAsyncAction('GET_REFUND_REQUESTS', {
  saveResult: true,
  initialValue: null,
  context: {
    request: ({ id, ccb }) =>
      telli.get(`/admin/contractloancustom/loans/${id}/refund-requests`, {
        params: ccb ? { ccb } : undefined,
        timeout: 15000,
      }),
    responseParser: parseRefundResourceResponse,
    errorParser: () => null,
  },
});

export const notifyRefundReceiptError = (err: { response?: { status?: number } }) => {
  const status = err?.response?.status;
  if (status === 404) {
    message.info('Comprovante de reembolso ainda não disponível para esta CCB');
    return;
  }
  if (status === 424) {
    message.warning('Comprovante de reembolso temporariamente indisponível. Tente novamente mais tarde.');
    return;
  }
  message.error('Falha ao baixar comprovante de reembolso');
};

export const downloadRefundReceipt = createAsyncAction('DOWNLOAD_REFUND_RECEIPT', {
  saveResult: true,
  context: {
    request: ({ id, ccb }) =>
      telli.get(`/admin/contractloancustom/loans/${id}/refund-receipt`, {
        params: ccb ? { ccb } : undefined,
        responseType: 'blob',
      }),
    onRequestError: notifyRefundReceiptError,
    responseParser: (res) => res,
  },
});

export const getPortOutAttackHistory = createAsyncAction('GET_PORT_OUT_ATTACK_HISTORY', {
  saveResult: true,
  initialValue: [],
  context: {
    request: ({ id }) => telli.get(`/admin/contractloancustom/loans/${id}/portout-attack-history`),
  },
});

export const resendToPayment = createAsyncAction('RESEND_TO_PAYMENT', {
  saveResult: true,
  context: {
    request: (payload) => telli.post(`/admin/contractcustom/contracts/${payload.contractId}/resubmitpayment`, payload),
  },
});

export const newResendToPayment = createAsyncAction('RESEND_TO_PAYMENT', {
  saveResult: true,
  context: {
    request: (payload) => telli.post(`/admin/mtproposal/${payload.contractId}/resubmitpayment`, payload),
  },
});

export const getContractFiles = createAsyncAction('GET_CONTRACT_FILES');

export const getContractFile = createAsyncAction('GET_CONTRACT_FILE', {
  saveResult: true,
  initialValue: {},
  context: {
    request: ({ id, document, file: { type, documentType } }) =>
      telli.post(`/admin/contractcustom/contracts/${id}/${document}?type=${type}&documenttype=${documentType}`),
  },
});

export const getContractBundleFiles = createAsyncAction('GET_CONTRACT_BUNDLE_FILES', {
  saveResult: true,
  context: {
    request: ({ id }) => telli.get(`/admin/contractcustom/contracts/${id}/contractfiles`, { responseType: 'blob' }),
    responseParser: (response) => response.data,
    onRequestSuccess: (file) => window.open(URL.createObjectURL(file)),
    onRequestError: () => message.error('Falha ao Baixar Arquivos.'),
  },
});

export const getContractInactiveFiles = createAsyncAction('GET_INACTIVE_CONTRACT_FILES', {
  saveResult: true,
  initialValue: [],
  context: {
    request: ({ id }) => telli.get(`/admin/contractcustom/contracts/${id}/inactivefiles`),
    errorParser: () => [],
  },
});

export const updateFinancialInstitutionKey = createAsyncAction('UPDATE_FINANCIAL_INSTITUTION_KEY', {
  context: {
    request: (payload) =>
      telli.post(`/admin/contractloancustom/loans/${payload.contractId}/updatefinancialinstitutionkey`, payload),
    onRequestSuccess: () => message.success('Número do contrato atualizado com sucesso!'),
    onRequestError: () => message.error('Falha ao atualizar número do contrato'),
  },
});

export const updateFinancialInstitutionKeyPort = createAsyncAction('UPDATE_FINANCIAL_INSTITUTION_KEY_PORT', {
  context: {
    request: (payload) =>
      telli.post(`/admin/contractloancustom/loans/${payload.contractId}/updatefinancialinstitutionkeyport`, payload),
    onRequestSuccess: () => message.success('Número do contrato de refin de portabilidade atualizado com sucesso!'),
    onRequestError: () => message.error('Falha ao atualizar número do contrato de refin de portabilidade'),
  },
});

export const updateOperationStep = createAsyncAction('UPDATE_OPERATION_STEP', {
  context: {
    request: (payload) => telli.post(`/admin/contractcustom/contracts/${payload.contractId}/operationstep`, payload),
    onRequestSuccess: () => message.success('Passo atualizado com sucesso!'),
    onRequestError: () => message.error('Falha ao atualizar a esteira'),
  },
});

export const fetchLoanTypes = createAsyncAction('LOAN_TYPES_FETCH_REQUEST', {
  initialValue: [],
  saveResult: true,
  context: {
    request: () => telli.get('/admin/contractcustom/loantypes'),
  },
});

export const fetchOperationStatusTypes = createAsyncAction('OPERATION_STATUS_TYPES_FETCH_REQUEST', {
  saveResult: true,
  initialValue: [],
  context: {
    request: () => telli.get('/admin/contractloancustom/operationstatustypes'),
  },
});

export const fetchLoanStatuses = createAsyncAction('LOAN_STATUSES_FETCH_REQUEST', {
  saveResult: true,
  initialValue: [],
  context: {
    request: () => telli.get('/admin/contractcustom/loanstatuses'),
    onRequestError: (e) => dialogErrors(e.messages),
  },
});

export const fetchOperationStepsTypes = createAsyncAction('OPERATION_STEPS_TYPES_FETCH_REQUEST', {
  initialValue: [],
  saveResult: true,
  context: {
    request: () => telli.get('/admin/contractloancustom/operationsteptypes'),
  },
});

export const cancelPreProposal = createAsyncAction('CANCEL_PREPROPOSAL_BY_CONTRACTS', {
  context: {
    request: (fileList) => {
      const formData = new FormData();

      const file = fileList[0];

      formData.append('file', file);

      return telli.post('/admin/contractcustom/cancelpreproposalbycontracts', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    additionalMeta: {
      errorMessage: 'Falha ao Fazer cancelamento de pré proposta.',
    },
    onRequestSuccess: () => message.success('Sucesso no Upload de CSV.'),
  },
});

export const uploadContractFile = createAsyncAction('UPLOAD_PERSON_FILE', {
  context: {
    request: (payload) => {
      const { uploadReason, comment, file, id } = payload;

      const reason = uploadReason.enum;
      const formData = new FormData();

      formData.append('file', file);
      formData.append('commentary', comment);
      formData.append('type', reason);

      return telli.post(`/admin/contractcustom/contracts/${id}/uploadfilepdf`, formData);
    },
    additionalMeta: {
      errorMessage: 'Falha ao enviar arquivos.',
    },
    onRequestSuccess: () => message.success('Sucesso no upload de arquivo.'),
  },
});

export const getContractInsurance = createAsyncAction('GET_CONTRACT_INSURANCE', {
  initialValue: {},
  saveResult: true,
  context: {
    request: ({ id }) => backoffice.get(`/insurance/contract/${id}`),
    responseParser: ({ data }) => data,
  },
});

export const getInsuranceDocument = createAsyncAction('GET_INSURANCE_DOCUMENT', {
  initialValue: {},
  saveResult: true,
  context: {
    responseParser: ({ data }) => data?.proposalInsurancePDF,
    onRequestError: () => message.warning('Nenhum arquivo disponível para download!'),
    request: ({ id, externalContractId, type, document, contractType }) =>
      backoffice.get(`/insurance/pdf`, {
        params: { id, externalContractId, type, document, contractType },
      }),
  },
});

export const getAuditTrailPdf = createAsyncAction('GET_AUDIT_TRAIL_PDF', {
  initialValue: null,
  saveResult: true,
  context: {
    responseParser: ({ data }) => data,
    onRequestError: (err) => {
      const status = err?.response?.status;
      if (status === 403) {
        message.warning('Você não tem permissão para baixar a trilha. Solicite a role LEGAL_TEAM ao gestor.');
      } else if (status === 404) {
        message.warning('Trilha de contratação não disponível para este contrato.');
      } else {
        message.error('Não foi possível carregar o PDF. Tente novamente em instantes.');
      }
    },
    request: ({ id }) =>
      telli.get(`/admin/audit-trail/download/standalone/${id}`, {
        responseType: 'blob',
      }),
  },
});

export const validateKYC = createAsyncAction('VALIDATE_KYC', {
  context: {
    request: ({ contractId }) => telli.post(`/admin/contractcustom/${contractId}/validatekyc`),
    onRequestError: () => message.error('Falha ao validar KYC'),
    onRequestSuccess: () => message.success('KYC validado com sucesso!'),
  },
});

export const getOperationSteps = createAsyncAction('GET_OPERATION_STEPS_BY_CONTRACT', {
  initialValue: [],
  saveResult: true,
  context: {
    request: () => backoffice.get(`/operationsteps`),
    onRequestError: () => message.error('Falha ao requisitar passos da esteira'),
    responseParser: ({ data }) => data,
  },
});

export const operationStep = createAsyncAction('OPERATION_STEP', {
  context: {
    request: (payload) => backoffice.patch(`/contracts/operationstep`, payload),
    onRequestError: () => message.error('Falha ao alterar o passo da esteira'),
    onRequestSuccess: () => message.success('Passo da esteira atualizado com sucesso!'),
  },
});

export const getCancellationReasons = createAsyncAction('GET_CANCELLATION_REASONS', {
  saveResult: true,
  context: {
    onRequestError: () => message.error('Erro ao tentar carregar os motivos de cancelamento'),
    responseParser: ({ data }) => data,
    request: ({ productName }) =>
      backoffice.get(`/reason-cancellation`, {
        params: { productName },
      }),
  },
});

export const cancelContract = createAsyncAction('CANCEL_CONTRACT', {
  context: {
    onRequestSuccess: () => message.success('Contrato cancelado com sucesso!'),
    onRequestError: () => message.error('Falha ao cancelar contrato'),
    request: (payload) => backoffice.post(`/contracts/cancel`, payload),
  },
});

export const getDetails = createAsyncAction('GET_DETAILS_CONTRACT', {
  saveResult: true,
  initialValue: {},
  context: {
    onRequestError: () => message.error('Erro ao tentar carregar os detalhes do contrato'),
    responseParser: ({ data }) => data,
    request: ({ id }) => backoffice.get(`/contracts/details/${id}`),
  },
});

export const getContractHistory = createAsyncAction('GET_CONTRACT_HISTORY', {
  saveResult: true,
  context: {
    onRequestError: () => message.error('Erro ao tentar carregar o histórico do contrato'),
    responseParser: ({ data }) => data,
    request: ({ externalId, product, document }) =>
      backoffice.get(`/contracts/history`, {
        params: { externalId, product, document },
      }),
  },
});

export const confirmKYC = createAsyncAction('CONFIRM_KYC', {
  context: {
    onRequestSuccess: () => message.success('KYC confirmado com sucesso!'),
    onRequestError: () => message.error('Falha ao confirmar KYC'),
    request: (data) => backoffice.post(`/contracts/approve-kyc`, data),
  },
});

export const approveContract = createAsyncAction('APPROVE_CONTRACT', {
  context: {
    onRequestSuccess: () => message.success('Contrato aprovado com sucesso!'),
    onRequestError: () => message.error('Falha ao aprovar contrato'),
    request: (data) => backoffice.post(`/contracts/approve`, data),
  },
});

export const downloadAuditTrail = createAsyncAction('DOWNLOAD_AUDIT_TRAIL', {
  saveResult: true,
  context: {
    request: ({ contractId }) =>
      telli.get(`/admin/audit-trail/download/${contractId}`, {
        responseType: 'blob',
      }),
    onRequestError: () => message.error('Falha ao baixar trilha de auditoria'),
    responseParser: (res) => res,
  },
});

export const downloadPortabilityTerm = createAsyncAction('DOWNLOAD_PORTABILITY_TERM', {
  saveResult: true,
  context: {
    request: ({ contractId }) =>
      telli.get(`/admin/portability-term/download/${contractId}`, {
        responseType: 'blob',
      }),
    onRequestError: () => message.error('Falha ao baixar o termo de portabilidade'),
    responseParser: (res) => res,
  },
});

export const getPayments = createAsyncAction('GET_PAYMENTS', {
  saveResult: true,
  initialValue: [],
  context: {
    errorParser: () => [],
    responseParser: ({ data }) => data,
    onRequestError: (err) =>
      handleErrors(err, {
        defaultMessage: 'Ocorreu um erro ao tentar carregar as parcelas',
        duration: 5,
        ignoreErrorStatus: [404],
      }),
    request: ({ document }) => backoffice.get(`/contracts/document/${document}`),
  },
});

export const getPaymentsByContractId = createAsyncAction('GET_PAYMENTS_BY_CONTRACT', {
  saveResult: true,
  initialValue: null,
  context: {
    errorParser: () => null,
    responseParser: ({ data }) => data,
    onRequestError: (err) =>
      handleErrors(err, {
        defaultMessage: 'Ocorreu um erro ao tentar carregar as parcelas',
        duration: 5,
        ignoreErrorStatus: [404],
      }),
    request: ({ contractId }) => backoffice.get(`/contracts/ccb/${contractId}`),
  },
});

export const getOverduePayments = createAsyncAction('GET_OVERDUE_PAYMENTS', {
  saveResult: true,
  initialValue: [],
  context: {
    errorParser: () => [],
    responseParser: ({ data }) => data,
    onRequestError: (err) =>
      handleErrors(err, {
        defaultMessage: 'Ocorreu um erro ao tentar carregar as parcelas',
        duration: 5,
        ignoreErrorStatus: [404],
      }),
    request: ({ document }) => backoffice.get(`/contracts/document/${document}/overdue`),
  },
});

export const getContractByCcb = createAsyncAction('GET_CONTRACT_BY_CCB', {
  saveResult: true,
  initialValue: null,
  context: {
    responseParser: ({ data }) => data,
    onRequestError: (err) =>
      handleErrors(err, {
        defaultMessage: 'Ocorreu um erro ao buscar contrato pelo CCB',
        duration: 5,
      }),
    request: ({ ccb }) => backoffice.get(`/contracts/ccb/${ccb}`),
  },
});

export const getInsuranceContractDetail = createAsyncAction('GET_INSURANCE_CONTRACT_DETAIL', {
  saveResult: true,
  initialValue: null as InsuranceContractDetail | null,
  context: {
    request: ({ contractId }: { contractId: number }) => backoffice.get(`/contracts/${contractId}/insurance-detail`),
    responseParser: ({ data }) => data,
  },
});

/**
 * O filtro `ccb` do mt-turing e busca PARCIAL (ver AdvanceResource/AdvanceIntegrationService no
 * mt-backoffice-backend): consultar a CCB base traz junto as antecipacoes das versoes de refin
 * (`...ADT1`, `...ADT2`), e a tela filtra a CCB exata depois. Com a ordenacao `creation` desc do
 * backend, uma pagina unica pode ser preenchida so por essas vizinhas e nao sobrar nenhuma linha
 * da CCB pedida -- e a caixa nao truncaria a lista, ela sumiria dizendo "sem antecipacao".
 *
 * Por isso a busca percorre as paginas em vez de confiar na primeira. O teto existe para a
 * consulta nao virar um fan-out sem limite se o prefixo casar com muita coisa; na pratica uma CCB
 * tem uma antecipacao por versao de refin, entao a primeira pagina ja resolve.
 */
const ADVANCES_PAGE_SIZE = 100;
const ADVANCES_MAX_PAGES = 10;

/**
 * Antecipacoes de recebiveis da CCB, com os termos ja aninhados (CORE-2637).
 *
 * O filtro e por `ccb` -- a antecipacao vive no mt-turing, que nao conhece o id de contrato do
 * backoffice. `errorParser: () => null` mantem o padrao das demais leituras de reembolso: falha de
 * carga vira ausencia de dado, e a tela decide o que mostrar.
 */
export const getAdvancesByCcb = createAsyncAction('GET_ADVANCES_BY_CCB', {
  saveResult: true,
  initialValue: null as AdvancePage | null,
  context: {
    request: async ({ ccb }: { ccb: string }) => {
      const fetchPage = (page: number) =>
        backoffice.get('/advances', { params: { ccb, page, size: ADVANCES_PAGE_SIZE }, timeout: 15000 });

      const first = await fetchPage(0);
      const totalPages = Math.min(first?.data?.totalPages ?? 1, ADVANCES_MAX_PAGES);
      if (totalPages <= 1) return first;

      const rest = await Promise.all(Array.from({ length: totalPages - 1 }, (_, i) => fetchPage(i + 1)));
      const content = [first, ...rest].flatMap((response) => response?.data?.content ?? []);

      return { ...first, data: { ...first.data, content } };
    },
    responseParser: ({ data }) => data,
    errorParser: () => null,
  },
});

/**
 * Catalogo de status com os rotulos PT-BR. Muda so quando o mt-turing muda o vocabulario, entao e
 * buscado uma vez por abertura da aba, nao por linha renderizada.
 */
export const getAdvanceStatuses = createAsyncAction('GET_ADVANCE_STATUSES', {
  saveResult: true,
  initialValue: null as AdvanceStatusCatalog | null,
  context: {
    request: () => backoffice.get('/advances/statuses', { timeout: 15000 }),
    responseParser: ({ data }) => data,
    errorParser: () => null,
  },
});

/**
 * Historico de transicoes de um termo -- a fonte das datas de etapa do drawer de detalhe.
 * Lista vazia e resposta legitima (termo anterior a essa funcionalidade do Turing).
 */
export const getAdvanceTermHistory = createAsyncAction('GET_ADVANCE_TERM_HISTORY', {
  saveResult: true,
  initialValue: null as AdvanceTermHistoryEntry[] | null,
  context: {
    request: ({ idTerm }: { idTerm: string }) =>
      backoffice.get(`/advances/terms/${idTerm}/history`, { timeout: 15000 }),
    responseParser: ({ data }) => data,
    errorParser: () => null,
  },
});

/**
 * Comprovante do Pix de um termo pago: nome do arquivo e url pre-assinada.
 *
 * Os dois 409 do Turing sao desfechos de NEGOCIO, nao falha de integracao, e por isso ganham
 * mensagem propria: TERM_NOT_PAID (nao houve Pix a comprovar) e PROOF_NOT_AVAILABLE (houve, mas o
 * recibo ainda nao chegou). Tratar os dois como "erro ao baixar" faria o operador abrir chamado
 * para uma situacao normal.
 */
export const getAdvanceTermProofOfPayment = createAsyncAction('GET_ADVANCE_TERM_PROOF_OF_PAYMENT', {
  saveResult: true,
  initialValue: null as AdvanceTermProofOfPayment | null,
  context: {
    request: ({ idTerm }: { idTerm: string }) =>
      backoffice.get(`/advances/terms/${idTerm}/proof-of-payment`, { timeout: 20000 }),
    responseParser: ({ data }) => data,
    onRequestError: (err: { response?: { status?: number; data?: { error?: string } } }) => {
      const { status, data } = err?.response ?? {};
      if (status === 409 && data?.error === 'TERM_NOT_PAID') {
        message.info('Ainda nao houve pagamento para comprovar neste termo');
        return;
      }
      if (status === 409 && data?.error === 'PROOF_NOT_AVAILABLE') {
        message.info('O pagamento foi feito, mas o comprovante ainda nao esta disponivel');
        return;
      }
      // O comprovante e gerado pela Parati, nao pela meutudo: quando aquele sistema nao responde,
      // dizer isso evita que o operador procure o problema no contrato ou abra chamado a toa.
      if (data?.error === 'PROOF_OF_PAYMENT_UPSTREAM_FAILURE') {
        message.error('O sistema da Parati nao respondeu ao pedido do comprovante. Tente novamente em alguns minutos.');
        return;
      }
      message.error('Falha ao buscar o comprovante de pagamento');
    },
  },
});

/**
 * Termos de formalizacao do contrato (mt-formalization, via BFF).
 *
 * Chaveado pelo contractId do CONTRATO, nao pelo id da antecipacao: sao dois sistemas distintos.
 * Contrato sem termos responde 200 com lista vazia -- nao e erro.
 */
export const getContractTerms = createAsyncAction('GET_CONTRACT_TERMS', {
  saveResult: true,
  initialValue: null as ContractTermsResult | null,
  context: {
    request: ({ contractId }: { contractId: string }) =>
      backoffice.get(`/contracts/${contractId}/terms`, { timeout: 15000 }),
    responseParser: ({ data }) => data,
    errorParser: () => null,
  },
});

/**
 * PDF do termo aceito. `responseType: 'blob'` porque o corpo sao os bytes do documento.
 *
 * O 400 do formalization (FORM-185) significa que nao ha aceite daquele termo neste contrato --
 * situacao de dado, nao falha de sistema, e por isso ganha mensagem propria.
 */
export const downloadContractTermPdf = createAsyncAction('DOWNLOAD_CONTRACT_TERM_PDF', {
  saveResult: true,
  context: {
    request: ({ contractId, termKey }: { contractId: string; termKey: string }) =>
      backoffice.get(`/contracts/${contractId}/terms/${termKey}/pdf`, {
        responseType: 'blob',
        timeout: 30000,
      }),
    responseParser: (res) => res,
    onRequestError: (err: { response?: { status?: number } }) => {
      if (err?.response?.status === 400) {
        message.info('Nao ha termo de antecipacao registrado para esta parcela');
        return;
      }
      message.error('Falha ao baixar o termo de antecipacao');
    },
  },
});
